import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useAuth } from "../../context/AuthContext";
import { setHubImageKey } from "../../store/gallerySlice";
import { Breadcrumb } from "../../components/Breadcrumb";
import axios from "axios";
import { encryptUserAndHubPayload } from "../../utils/encryption";
import { dbUserAndHub } from "../../config/api";
import {
  cdnUrl,
  cdnUrlForStorageKey,
  basenameFromStorageKey,
} from "../../config/mediaPaths";
import {
  App as AntApp,
  Spin,
  Tabs,
  Typography,
} from "antd";
import { RightOutlined, BankOutlined } from "@ant-design/icons";
import GallerySegmentPanel from "./GallerySegmentPanel";
import { isDeletedBiz } from "./galleryBizUtils";
import PageContainer from "../../components/PageContainer/PageContainer";
import styles from "./MediaGallery.module.css";
import {
  resolveCatalogEntitlement,
  calculateGalleryImageLimit,
} from "../../services/subscriptionCatalog";
import { getHubPlanId } from "../../utils/subscriptionState";

const { Text } = Typography;

function normalizeHubKey(hubKey) {
  return (hubKey || "").replace(/^HubId:/, "");
}

function getHubLabel(profile) {
  if (!profile) return "Unknown Hub";
  return profile.DisplayName || profile.Domain || profile.PK;
}

const MediaGallery = () => {
  const { message } = AntApp.useApp();
  const dispatch = useDispatch();
  const { token, decodeToken, subscriptionCatalog } = useAuth();
  const decodedToken = useMemo(() => (token ? decodeToken(token) : null), [token, decodeToken]);

  const hubProfiles = useMemo(() => {
    const profiles = [];
    const addProfile = (profile) => {
      if (!profile) return;
      profiles.push(profile);
    };
    const myHubAdmin = decodedToken?.MyHubAdmin;
    if (Array.isArray(myHubAdmin)) {
      myHubAdmin.forEach((entry) => {
        const hubProfile = entry?.HubProfile;
        if (Array.isArray(hubProfile)) hubProfile.forEach(addProfile);
        else addProfile(hubProfile);
      });
    }
    if (Array.isArray(decodedToken?.StaffAssignments)) {
      decodedToken.StaffAssignments.forEach((a) => addProfile(a?.HubProfile));
    }
    const uniqueByPk = new Map();
    profiles.forEach((p) => {
      const pk = p?.PK;
      if (pk && !uniqueByPk.has(pk)) uniqueByPk.set(pk, p);
    });
    return Array.from(uniqueByPk.values());
  }, [decodedToken]);

  const [selectedHubId, setSelectedHubId] = useState("");
  const [shops, setShops] = useState([]);
  const [hubImagesByHubKey, setHubImagesByHubKey] = useState({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedBizId, setSelectedBizId] = useState("");
  const [expandedHubs, setExpandedHubs] = useState({});
  const [galleryTab, setGalleryTab] = useState("biz");
  useEffect(() => {
    if (!selectedHubId && hubProfiles.length > 0) {
      const first = hubProfiles[0]?.PK || "";
      setSelectedHubId(first);
      setExpandedHubs({ [first]: true });
    }
  }, [hubProfiles, selectedHubId]);

  const refreshShops = useCallback(
    async (options = {}) => {
      const silent = options.silent === true;
      if (!token) return;
      if (!silent) setInitialLoading(true);
      try {
        const response = await axios.post(
          dbUserAndHub,
          encryptUserAndHubPayload({
            operationType: "read",
            action: "fetchHubs",
            token,
          }),
          { headers: { "Content-Type": "application/json" } }
        );
        const data = response.data || {};
        const adminHubs = data.adminHubs || {};
        const staffHubs = data.staffHubs || {};
        const list = [];

        const pushStores = (hubMap) => {
          Object.entries(hubMap).forEach(([hubKey, stores]) => {
            const hubId = normalizeHubKey(hubKey);
            (stores || []).forEach((store) => {
              if (isDeletedBiz(store)) return;
              const bs = store.BizSettings || {};
              const bizPrivate = Boolean(store.IsPrivate);
              const storageSalt = bs.storageSalt ?? bs.StorageSalt ?? store.storageSalt ?? store.StorageSalt ?? "";
              const storageIv = bs.storageIv ?? bs.StorageIV ?? store.storageIv ?? store.StorageIV ?? "";
              list.push({
                bizId: store.BizId,
                hubId,
                name: store.BizName,
                storeType: store.BizType ?? store.bizType ?? "",
                version: store.Version,
                bizImageKey: store.BizImageKey || "",
                bizPrivate,
                storageSalt,
                storageIv,
              });
            });
          });
        };
        pushStores(adminHubs);
        pushStores(staffHubs);
        setShops(list);
        setHubImagesByHubKey(data.hubImagesByHubKey || {});
      } catch (e) {
        console.error(e);
        message.error("Could not load Biz list.");
      } finally {
        if (!silent) setInitialLoading(false);
      }
    },
    [token, message]
  );

  useEffect(() => {
    refreshShops();
  }, [refreshShops]);

  useEffect(() => {
    if (!selectedHubId) return;
    const hk = hubImagesByHubKey[`HubId:${selectedHubId}`];
    if (hk) dispatch(setHubImageKey(hk));
  }, [selectedHubId, hubImagesByHubKey, dispatch]);

  const shopsForHub = useMemo(
    () => shops.filter((s) => !selectedHubId || s.hubId === selectedHubId),
    [shops, selectedHubId]
  );

  const selectedShop = useMemo(
    () => shopsForHub.find((s) => s.bizId === selectedBizId) || null,
    [shopsForHub, selectedBizId]
  );
  const selectedHubProfile = useMemo(
    () => hubProfiles.find((profile) => profile?.PK === selectedHubId) || null,
    [hubProfiles, selectedHubId]
  );
  const selectedHubRole = useMemo(
    () => (decodedToken?.MyHubAdmin || []).some(
      (entry) => entry?.HubProfile?.PK === selectedHubId
    ) ? "owner" : "staff",
    [decodedToken, selectedHubId]
  );
  const galleryEntitlement = useMemo(
    () => subscriptionCatalog && selectedHubProfile
      ? resolveCatalogEntitlement(
          subscriptionCatalog,
          getHubPlanId(selectedHubProfile),
          { moduleId: "gallery", limitId: "imagesPerItem" },
          selectedHubRole
        )
      : null,
    [
      selectedHubProfile,
      selectedHubRole,
      subscriptionCatalog,
    ]
  );
  const imageLimit = galleryEntitlement?.allowed
    ? calculateGalleryImageLimit(galleryEntitlement.plan) ?? 0
    : 0;

  const storeType = selectedShop?.storeType ?? "";
  const galleryManifestCrypto = null;

  const hubDisplayKey = selectedHubId
    ? hubImagesByHubKey[`HubId:${selectedHubId}`] || ""
    : "";

  const handleSelectHub = (hubId) => {
    setSelectedHubId(hubId);
    setSelectedBizId("");
    setGalleryTab("biz");
    setExpandedHubs((prev) => ({ ...prev, [hubId]: !prev[hubId] }));
  };

  const handleSelectBiz = (bizId) => {
    setSelectedBizId(bizId);
    setGalleryTab("biz");
  };

  const bizThumbUrl = (shop) => {
    if (!shop?.bizImageKey) return null;
    if (shop.hubId && shop.bizId) {
      return cdnUrl({ entity: "biz", fileType: "media", hubId: shop.hubId, bizId: shop.bizId, fileName: basenameFromStorageKey(shop.bizImageKey) });
    }
    return cdnUrlForStorageKey(shop.bizImageKey);
  };

  const shopsByHub = useMemo(() => {
    const map = {};
    shops.forEach((s) => {
      if (!map[s.hubId]) map[s.hubId] = [];
      map[s.hubId].push(s);
    });
    return map;
  }, [shops]);

  const totalBiz = shops.length;
  const totalHubs = hubProfiles.length;
  const showTabs = !!selectedBizId;

  const getInitials = (name) => {
    return (name || "").replace(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase() || "?";
  };

  return (
    <PageContainer>
      <Breadcrumb items={[{ label: "Media Gallery" }]} />
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        Media Gallery
      </Typography.Title>

      {initialLoading ? (
        <div className={styles.loadingState} style={{ padding: 48, textAlign: "center" }}>
          <Spin />
        </div>
      ) : (
        <div className={styles.workspace}>
          <div className={styles.sidebar}>
            <div className={styles.sidebarTitle}>
              <span>Hubs & Businesses</span>
              <span className={styles.sidebarCount}>
                {totalHubs} hub{totalHubs !== 1 ? "s" : ""} · {totalBiz} biz
              </span>
            </div>
            <div className={styles.sidebarList}>
              {hubProfiles.map((hub) => {
                const hubPk = hub?.PK;
                const isExpanded = expandedHubs[hubPk];
                const isHubSelected = selectedHubId === hubPk && !selectedBizId;
                const hubBizList = shopsByHub[hubPk] || [];
                const hasBiz = hubBizList.length > 0;
                return (
                  <div key={hubPk} className={styles.hubGroup}>
                    <div
                      className={`${styles.hubHeader} ${isHubSelected ? styles.hubHeaderActive : ""}`}
                      onClick={() => handleSelectHub(hubPk)}
                    >
                      {hasBiz && (
                        <span className={`${styles.hubArrow} ${isExpanded ? styles.hubArrowExpanded : ""}`}>
                          <RightOutlined style={{ fontSize: 10 }} />
                        </span>
                      )}
                      {!hasBiz && <span style={{ width: 14, flexShrink: 0 }} />}
                      <BankOutlined className={styles.hubIcon} />
                      <span className={styles.hubName}>{getHubLabel(hub)}</span>
                      {hasBiz && <span className={styles.bizCountBadge}>{hubBizList.length}</span>}
                    </div>
                    {isExpanded && hasBiz && (
                      <div className={styles.bizList}>
                        {hubBizList.map((shop) => {
                          const isBizSelected = selectedBizId === shop.bizId;
                          const thumb = bizThumbUrl(shop);
                          const initials = getInitials(shop.name);
                          return (
                            <div
                              key={shop.bizId}
                              className={`${styles.bizItem} ${isBizSelected ? styles.bizItemActive : ""}`}
                              onClick={() => {
                                setSelectedHubId(hubPk);
                                handleSelectBiz(shop.bizId);
                              }}
                            >
                              {thumb ? (
                                <img
                                  src={thumb}
                                  alt=""
                                  className={styles.bizThumb}
                                />
                              ) : (
                                <div className={styles.bizThumbPlaceholder}>
                                  <span className={styles.bizThumbInitials}>{initials}</span>
                                </div>
                              )}
                              <span className={styles.bizName}>{shop.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {hubProfiles.length === 0 && (
                <div className={styles.emptyState} style={{ padding: 24, textAlign: "center" }}>
                  <Text type="secondary">No hubs available.</Text>
                </div>
              )}
            </div>
          </div>

          <div className={styles.panel} data-testid="gallery-panel">
            {!selectedHubId ? (
              <div className={styles.emptyState}>
                <Text type="secondary">Select a hub or business from the sidebar.</Text>
              </div>
            ) : showTabs ? (
              <Tabs activeKey={galleryTab} onChange={setGalleryTab} style={{ padding: "0 16px", marginTop: 8 }}>
                <Tabs.TabPane tab="Biz media" key="biz">
                  <GallerySegmentPanel
                    token={token}
                    hubId={selectedHubId}
                    bizId={selectedBizId}
                    segment="biz"
                    storeType={storeType}
                    bizVersion={selectedShop?.version}
                    bizImageKeyFromDb={selectedShop?.bizImageKey ?? ""}
                    compact={false}
                    onBizBrandingSaved={() => refreshShops({ silent: true })}
                    manifestCrypto={galleryManifestCrypto}
                    bizPrivate={!!selectedShop?.bizPrivate}
                    imageLimit={imageLimit}
                  />
                </Tabs.TabPane>
                <Tabs.TabPane tab="Items media" key="items">
                  <GallerySegmentPanel
                    token={token}
                    hubId={selectedHubId}
                    bizId={selectedBizId}
                    segment="items"
                    storeType={storeType}
                    compact={false}
                    manifestCrypto={galleryManifestCrypto}
                    bizPrivate={!!selectedShop?.bizPrivate}
                    imageLimit={imageLimit}
                  />
                </Tabs.TabPane>
              </Tabs>
            ) : (
              <div className={styles.panelContent}>
                <GallerySegmentPanel
                  token={token}
                  hubId={selectedHubId}
                  segment="hub"
                  hubImageKeyFromDb={hubDisplayKey}
                  onHubImageSaved={() => refreshShops({ silent: true })}
                  compact={false}
                  imageLimit={imageLimit}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default MediaGallery;
